import AddressSearchStatus from "./Address/AddressSearchStatus";
import React, { useState, useEffect, useRef, useCallback } from "react";
import styled from "styled-components";
import { useDispatch, useSelector } from "react-redux";
import { motion, AnimatePresence } from "framer-motion";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import MyLocationIcon from "@mui/icons-material/MyLocation";
import CloseIcon from "@mui/icons-material/Close";
import EditLocationIcon from "@mui/icons-material/EditLocation";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import CircularProgress from "@mui/material/CircularProgress";
import GpsFixedIcon from "@mui/icons-material/GpsFixed";
import SatelliteAltIcon from "@mui/icons-material/SatelliteAlt";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";


import NearMeIcon from "@mui/icons-material/NearMe";
import useMarketPlacesAutocomplete from "../hooks/useMarketPlacesAutocomplete";
import { geocodeAddress, latLngFromGeocode } from "../utils/googlePlaces";
import reportClientError from "../utils/clientTelemetry";
import { trackLocationPromptView, surfaceForPath } from "../utils/analytics";
import { useLocation } from "react-router-dom";
import {
  Combobox,
  ComboboxInput,
  ComboboxPopover,
  ComboboxList,
  ComboboxOption,
} from "@reach/combobox";
import {
  getCurrentLocation,
  getLocationByIP,
  clearLocation,
  setManualAddress,
  setHasSeenPrompt,
  reconfirmLocation,
  confirmIpSuggestion,
  dismissIpSuggestion,
  ACCURACY_WARN_M,
} from "../redux/userLocationSlice";
import {
  LOCATION_PROMPT_EXAMPLE,
  COUNTRY_NAME,
  isWithinMarketBounds,
} from "../config/market";

const addressPlaceholder = LOCATION_PROMPT_EXAMPLE;

const LocationPrompt = ({ onClose, compact = false, open = false }) => {
  const routerLocation = useLocation();
  const dispatch = useDispatch();
  const {
    latitude, longitude, address, accuracy, isLoading, error,
    permissionStatus, locationSource, ipFallbackAttempted,
    ipSuggestion, needsReconfirm,
  } = useSelector((state) => state.userLocation);
  const [showPrompt, setShowPrompt] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  // Local error for a manual pick we refused to save (geocode failure or
  // out-of-market coordinates) — kept separate from the slice's GPS/IP error.
  const [manualPickError, setManualPickError] = useState(null);
  const dropdownRef = useRef(null);
  const explicitDeviceLookup = useRef(false);
  const triggerRef = useRef(null);

  // Google Maps Autocomplete
  // No `types` filter — return ALL result types (addresses, establishments, landmarks)
  // so users can search for precise places like "Egret Pilgrims Inn" as well as
  // general areas like "Kyaliwajjala trading center".
  const {
    value: manualAddressInput,
    setValue: setManualAddressInput,
    ready,
    suggestions: { data: suggestions = [], loading: addressLoading, status: addressStatus } = {},
    clearSuggestions,
    error: placesError,
  } = useMarketPlacesAutocomplete({ debounce: 100 });

  useEffect(() => {
    if (placesError) {
      setManualPickError(
        "Address search is temporarily unavailable. You can retry or use your device location."
      );
    }
  }, [placesError]);

  useEffect(() => {
    // The prompt opens only when the shopper asks for it (open=true from a
    // cart, storefront or checkout "Set delivery location" action). It used to
    // open by itself on a first visit, on every page that renders the header
    // chip, before the shopper had seen anything worth a location (2 Nov
    // 2026). Location is now asked for where it changes what they see: the
    // delivery fee and coverage in the cart, and the header chip.
    if (open) {
      setShowPrompt(true);
      trackLocationPromptView(surfaceForPath(routerLocation.pathname), surfaceForPath(routerLocation.pathname));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Auto-trigger IP fallback when permission was previously denied and no location exists
  useEffect(() => {
    if (
      !latitude &&
      !longitude &&
      !isLoading &&
      !ipFallbackAttempted &&
      !explicitDeviceLookup.current &&
      permissionStatus === 'denied'
    ) {
      dispatch(getLocationByIP());
    }
  }, [permissionStatus, latitude, longitude, isLoading, ipFallbackAttempted, dispatch]);

  // Close dropdown when clicking outside — but NOT while GPS is loading
  // (the browser's native permission dialog would trigger click-outside)
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        !isLoading
      ) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isLoading]);

  // Auto-retry GPS when user enables permission externally (via browser settings)
  useEffect(() => {
    if (!navigator.permissions) return;
    let permStatus;
    navigator.permissions.query({ name: 'geolocation' }).then((status) => {
      permStatus = status;
      status.onchange = () => {
        if (status.state === 'granted' && !latitude && !isLoading) {
          dispatch(getCurrentLocation({ allowIpFallback: !explicitDeviceLookup.current }));
        }
      };
    }).catch(() => { });
    return () => {
      if (permStatus) permStatus.onchange = null;
    };
  }, [latitude, isLoading, dispatch]);

  // Detect user platform for context-specific instructions
  const getPlatformInstructions = () => {
    const ua = navigator.userAgent;
    if (/Macintosh|MacIntel/.test(ua)) {
      return {
        platform: 'macOS',
        systemSteps: 'System Settings → Privacy & Security → Location Services → turn ON the master toggle at the top.',
        browserSteps: 'Click the \u24d8 icon in the address bar → "Reset permission" → then reload this page.',
      };
    }
    if (/Windows/.test(ua)) {
      return {
        platform: 'Windows',
        systemSteps: 'Settings → Privacy & Security → Location → turn on "Location services".',
        browserSteps: 'Click the \ud83d\udd12 icon in the address bar → Site settings → Location → Allow.',
      };
    }
    if (/Android/.test(ua)) {
      return {
        platform: 'Android',
        systemSteps: 'Settings → Location → turn on "Use location".',
        browserSteps: 'Tap the \ud83d\udd12 icon in the address bar → Permissions → Location → Allow.',
      };
    }
    if (/iPhone|iPad/.test(ua)) {
      return {
        platform: 'iOS',
        systemSteps: 'Settings → Privacy & Security → Location Services → turn on for Safari/Chrome.',
        browserSteps: 'Settings app → Safari (or Chrome) → Location → "While Using".',
      };
    }
    return {
      platform: 'your device',
      systemSteps: 'Open your device settings and enable Location Services.',
      browserSteps: 'Click the lock/info icon in your browser address bar → allow Location.',
    };
  };

  const [gpsAttemptFailed, setGpsAttemptFailed] = useState(false);

  const handleGetLocation = async () => {
    explicitDeviceLookup.current = true;
    try {
      setGpsAttemptFailed(false);
      const position = await dispatch(getCurrentLocation({ allowIpFallback: false }));
      setGpsAttemptFailed(!position?.coords);
      // Don't close dropdown — let user see the result
    } catch (e) {
      setGpsAttemptFailed(true);
      console.error("Failed to get location:", e);
    }
  };

  const handleClose = () => {
    setShowPrompt(false);
    dispatch(setHasSeenPrompt(true));
    if (onClose) onClose();
  };

  // When the modal was opened programmatically (open=true, e.g. from the cart's
  // "Set delivery location"), auto-close once the user actually picks a location.
  // Because `open` intentionally bypasses the first-visit `!latitude` gate, the
  // modal would otherwise stay up after a selection. We record the latitude that
  // was present when it opened and close as soon as it changes.
  const openLatRef = useRef(null);
  useEffect(() => {
    if (!open) {
      openLatRef.current = null;
      return;
    }
    if (openLatRef.current === null) {
      openLatRef.current = latitude == null ? "none" : String(latitude);
      return;
    }
    if (latitude != null && String(latitude) !== openLatRef.current) {
      openLatRef.current = null;
      handleClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, latitude]);

  const handleClearLocation = (e) => {
    e?.stopPropagation();
    dispatch(clearLocation());
    setShowDropdown(false);
  };

  const handleConfirmIpSuggestion = () => {
    dispatch(confirmIpSuggestion());
    setShowDropdown(false);
  };

  const handleDismissIpSuggestion = () => {
    dispatch(dismissIpSuggestion());
    // Keep dropdown open so user can search manually
  };

  // Stale-location re-confirm ("Still at X?")
  const handleReconfirmLocation = () => {
    dispatch(reconfirmLocation());
  };
  const handleUpdateStaleLocation = () => {
    dispatch(clearLocation());
    // Keep dropdown open — the user picks GPS or searches next.
  };

  // A GPS fix can be a coarse cell-tower estimate hundreds of metres off —
  // enough to flip a vendor's verdict near the edge of its radius. Flag it.
  const isLowAccuracyGps =
    locationSource === 'gps' &&
    Number.isFinite(Number(accuracy)) &&
    Number(accuracy) > ACCURACY_WARN_M;
  const isApproxLocation = locationSource === 'ip' || isLowAccuracyGps;

  const handleManualAddressSelect = async (selectedAddress) => {
    setManualAddressInput(selectedAddress, false);
    clearSuggestions();
    setManualPickError(null);

    try {
      const results = await geocodeAddress(selectedAddress);
      const { lat, lng } = latLngFromGeocode(results[0]);

      if (!Number.isFinite(Number(lat)) || !Number.isFinite(Number(lng))) {
        throw new Error("Geocode returned no usable coordinates");
      }
      if (!isWithinMarketBounds(lat, lng)) {
        // Places is country-restricted, but geocoding a free-typed string can
        // still land abroad — refuse rather than persist a useless location.
        setManualPickError(
          `That place appears to be outside ${COUNTRY_NAME}. Please pick a delivery location within ${COUNTRY_NAME}.`
        );
        return;
      }

      dispatch(setManualAddress({ address: selectedAddress, latitude: lat, longitude: lng }));
      setGpsAttemptFailed(false);
      setShowDropdown(false);
    } catch (error) {
      reportClientError(error, { area: "location_prompt_geocode" });
      // Do NOT save a coordless address — the navbar would show a location
      // while every coverage check reports "Location not set". Surface the
      // failure and let the user pick another suggestion or use GPS.
      setManualPickError(
        "We couldn't pin that address on the map. Please pick another nearby place or use GPS."
      );
    }
  };

  const toggleDropdown = () => {
    if (!showDropdown) trackLocationPromptView(surfaceForPath(routerLocation.pathname), "header");
    setShowDropdown(!showDropdown);
  };

  // Calculate dropdown position for mobile (fixed positioning needs explicit top)
  const getDropdownStyle = useCallback(() => {
    if (typeof window === 'undefined' || window.innerWidth > 480) return {};
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      return { top: rect.bottom + 8 };
    }
    return {};
  }, []);

  // Compact version for navbar/header with dropdown
  if (compact) {
    return (
      <CompactWrapper ref={dropdownRef}>
        <CompactContainer ref={triggerRef} onClick={toggleDropdown}>
          {isLoading ? (
            <CircularProgress size={16} style={{ color: "#1976d2" }} />
          ) : latitude && longitude ? (
            <>
              <LocationOnIcon
                style={{
                  fontSize: 18,
                  color: needsReconfirm || isApproxLocation ? '#ff9800' : '#50fb01ff',
                }}
              />
              <CompactText title={needsReconfirm ? `Still at ${address || 'this location'}? Tap to confirm.` : undefined}>
                {needsReconfirm ? 'Still at ' : isApproxLocation ? '≈ ' : ''}
                {address || `${latitude.toFixed(4)}, ${longitude.toFixed(4)}`}
                {needsReconfirm ? '?' : ''}
              </CompactText>
              <KeyboardArrowDownIcon style={{ fontSize: 18, color: "#fcf3f3ff" }} />
            </>
          ) : (
            <>
              <MyLocationIcon style={{ fontSize: 18, color: "#64fa29ff" }} />
              <CompactText $clickable>Set Your Location</CompactText>
              <KeyboardArrowDownIcon style={{ fontSize: 18, color: "#fff" }} />
            </>
          )}
        </CompactContainer>

        <AnimatePresence>
          {showDropdown && (
            <DropdownMenu
              style={showDropdown ? getDropdownStyle() : undefined}
              as={motion.div}
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.15 }}
            >
              <DropdownHeader>
                <EditLocationIcon style={{ fontSize: 20, color: "#1976d2" }} />
                <span>Your Delivery Location</span>
              </DropdownHeader>

              {(latitude && longitude) && (
                <CurrentLocationDisplay>
                  <LocationOnIcon style={{ fontSize: 16, color: isApproxLocation ? '#ff9800' : '#4caf50' }} />
                  <span><small style={{ display: "block", fontSize: 12, marginBottom: 3 }}>{locationSource === "gps" && !gpsAttemptFailed ? "Device location" : "Selected delivery area"}</small>{address || `${latitude.toFixed(4)}, ${longitude.toFixed(4)}`}</span>
                  {locationSource === 'ip' && (
                    <ApproxBadge>≈ Approx</ApproxBadge>
                  )}
                  {isLowAccuracyGps && (
                    <ApproxBadge title={`GPS was only accurate to ~${Math.round(accuracy)} m — delivery matching near a vendor's zone edge may be off. Retry for a sharper fix.`}>
                      ≈ ±{Math.round(accuracy)} m
                    </ApproxBadge>
                  )}
                  <ClearButtonSmall onClick={handleClearLocation}>
                    <CloseIcon style={{ fontSize: 14 }} />
                  </ClearButtonSmall>
                </CurrentLocationDisplay>
              )}

              {/* Stale rehydrated location — ask before trusting it */}
              {needsReconfirm && latitude && longitude && (
                <ReconfirmCard>
                  <ReconfirmHeader>
                    Still at {address || 'this location'}?
                  </ReconfirmHeader>
                  <ReconfirmNote>
                    You set this a while ago. Delivery coverage is checked
                    against it, so make sure it's still where you are.
                  </ReconfirmNote>
                  <IpSuggestionActions>
                    <IpConfirmButton onClick={handleReconfirmLocation}>
                      Yes, still here
                    </IpConfirmButton>
                    <IpDismissButton onClick={handleUpdateStaleLocation}>
                      No, update it
                    </IpDismissButton>
                  </IpSuggestionActions>
                </ReconfirmCard>
              )}

              {/* IP Suggestion awaiting confirmation */}
              {ipSuggestion && !latitude && (
                <IpSuggestionCard>
                  <IpSuggestionHeader>
                    <NearMeIcon style={{ fontSize: 16, marginRight: 6, verticalAlign: 'text-bottom' }} />
                    Are you near this area?
                  </IpSuggestionHeader>
                  <IpSuggestionAddress>{ipSuggestion.address}</IpSuggestionAddress>
                  <IpSuggestionNote>
                    Detected via your network — this is approximate
                  </IpSuggestionNote>
                  <IpSuggestionActions>
                    <IpConfirmButton onClick={handleConfirmIpSuggestion}>
                      Yes, close enough
                    </IpConfirmButton>
                    <IpDismissButton onClick={handleDismissIpSuggestion}>
                      No, let me search
                    </IpDismissButton>
                  </IpSuggestionActions>
                  <GpsNudge>
                    <SatelliteAltIcon style={{ fontSize: 16, color: '#81c784', flexShrink: 0, marginTop: 1 }} />
                    <GpsNudgeText>
                      <strong>Want to use your device’s location?</strong> Tap "Use Current Location"
                      below and allow location access for a fresh device reading.
                    </GpsNudgeText>
                  </GpsNudge>
                </IpSuggestionCard>
              )}

              <DropdownButton onClick={handleGetLocation} disabled={isLoading}>
                {isLoading ? (
                  <>
                    <CircularProgress size={16} style={{ color: "#1976d2", marginRight: 8 }} />
                    <span>Detecting Location...</span>
                  </>
                ) : (
                  <>
                    <MyLocationIcon style={{ fontSize: 18, marginRight: 8 }} />
                    <span>Use Current Location</span>
                  </>
                )}
              </DropdownButton>
              {!isLoading && (
                <GpsPrecisionTag>
                  <GpsFixedIcon style={{ fontSize: 12, marginRight: 4, verticalAlign: 'text-bottom' }} />
                  {locationSource === "gps" && Number(accuracy) > 0 && !gpsAttemptFailed && !error
                    ? `Device-reported accuracy: about ${Math.round(accuracy)}m`
                    : "Uses your device location; accuracy varies"}
                </GpsPrecisionTag>
              )}

              {permissionStatus !== 'denied' && (error || gpsAttemptFailed) && (
                <ErrorText role="status">
                  {error || "We could not get a fresh device location."}{" "}
                  {latitude ? "Your selected delivery area has not changed. " : ""}
                  Search for your delivery address below, or enable device location and retry.
                </ErrorText>
              )}
              {permissionStatus === "denied" && (
                <PermissionDeniedCard>
                  {(() => {
                    const info = getPlatformInstructions();
                    return (
                      <>
                        <PermissionDeniedRow>
                          <LockOutlinedIcon style={{ fontSize: 16, color: '#ff9800', flexShrink: 0, marginTop: 2 }} />
                          <PermissionDeniedText>
                            Location is blocked. Enable it in <strong>both</strong> places below:
                          </PermissionDeniedText>
                        </PermissionDeniedRow>
                        <InstructionSteps>
                          <InstructionStep>
                            <StepNumber>1</StepNumber>
                            <StepText><strong>Device:</strong> {info.systemSteps}</StepText>
                          </InstructionStep>
                          <InstructionStep>
                            <StepNumber>2</StepNumber>
                            <StepText><strong>Browser:</strong> {info.browserSteps}</StepText>
                          </InstructionStep>
                          <InstructionStep>
                            <StepNumber>3</StepNumber>
                            <StepText>Come back here and tap <strong>Retry</strong> below.</StepText>
                          </InstructionStep>
                        </InstructionSteps>
                        <EnableGpsButton onClick={handleGetLocation}>
                          <MyLocationIcon style={{ fontSize: 16, marginRight: 6 }} />
                          Retry Location
                        </EnableGpsButton>
                      </>
                    );
                  })()}
                </PermissionDeniedCard>
              )}

              <Divider />

              <ManualInputLabel>Or enter your area:</ManualInputLabel>
              <Combobox onSelect={handleManualAddressSelect}>
                <div style={{ position: "relative" }}>
                  <ComboboxInput
                    value={manualAddressInput}
                    onChange={(e) => {
                      setManualAddressInput(e.target.value);
                      if (manualPickError) setManualPickError(null);
                    }}
                    disabled={!ready}
                    placeholder={addressPlaceholder}
                    style={{
                      width: "100%",
                      padding: "10px",
                      border: "1px solid #ddd",
                      borderRadius: "6px",
                      fontSize: "0.85rem",
                      outline: "none",
                      boxSizing: "border-box",
                    }}
                  />
                  <AddressSuggestions
                    portal={false}
                    style={{
                      backgroundColor: "white",
                      margin: "0",
                      padding: "0",
                      fontSize: "12px",
                      border: "1px solid #ddd",
                      boxShadow: "0 0 10px 0 rgba(0, 0, 0, 0.1)",
                      borderBottomRightRadius: "6px",
                      borderBottomLeftRadius: "6px",
                      zIndex: 10001,
                      position: "relative",
                      width: "100%",
                    }}
                  >
                    <ComboboxList
                      style={{
                        listStyle: "none",
                        margin: 0,
                        padding: 0,
                      }}
                    >
                      {suggestions.map(({ place_id, description }) => (
                        <ComboboxOption
                          key={place_id}
                          value={description}
                          style={{
                            padding: "8px 12px",
                            cursor: "pointer",
                            borderBottom: "1px solid #eee",
                          }}
                          onMouseDown={(e) => {
                            e.preventDefault();
                            handleManualAddressSelect(description);
                          }}
                        />
                      ))}
                    </ComboboxList>
                  </AddressSuggestions>
                </div>
              </Combobox>
                  <AddressSearchStatus loading={addressLoading} ready={ready} status={addressStatus} count={suggestions.length} error={placesError} />
              {manualPickError && <ErrorText>{manualPickError}</ErrorText>}
            </DropdownMenu>
          )}
        </AnimatePresence>
      </CompactWrapper>
    );
  }

  // Full prompt modal — shows on first visit OR when a parent opens it (open).
  return (
    <AnimatePresence>
      {((showPrompt && !latitude) || open) && (
        <Overlay
          as={motion.div}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <PromptCard
            as={motion.div}
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
          >
            <CloseButton onClick={handleClose}>
              <CloseIcon />
            </CloseButton>

            <IconWrapper>
              <LocationOnIcon style={{ fontSize: 48, color: "#1976d2" }} />
            </IconWrapper>

            <Title>Enable Location Services</Title>

            <Description>
              Allow Lusuku to access your location to show you vendors that deliver to your area.
            </Description>

            {error && (
              <ErrorMessage>
                {error}
              </ErrorMessage>
            )}

            {permissionStatus === "denied" && (
              <PermissionNote>
                Location access was denied. We'll try to detect your area automatically.
                For precise delivery matching, you can also search for your address below.
              </PermissionNote>
            )}

            {/* IP Suggestion in full modal */}
            {ipSuggestion && !latitude && (
              <ApproxLocationBanner>
                <span>
                  <NearMeIcon style={{ fontSize: 16, marginRight: 6, verticalAlign: 'text-bottom' }} />
                  Are you near <strong>{ipSuggestion.address}</strong>?
                </span>
                <ApproxSubtext>
                  Detected via your network — this is approximate.
                </ApproxSubtext>
                <IpSuggestionActions style={{ marginTop: '10px' }}>
                  <IpConfirmButton onClick={handleConfirmIpSuggestion}>
                    Yes, close enough
                  </IpConfirmButton>
                  <IpDismissButton onClick={handleDismissIpSuggestion}>
                    No, let me search
                  </IpDismissButton>
                </IpSuggestionActions>
              </ApproxLocationBanner>
            )}

            <ButtonGroup>
              <PrimaryButton
                onClick={handleGetLocation}
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <CircularProgress size={20} style={{ color: "white", marginRight: 8 }} />
                    Detecting Location...
                  </>
                ) : (
                  <>
                    <MyLocationIcon style={{ marginRight: 8 }} />
                    Use My Location
                  </>
                )}
              </PrimaryButton>

              {/* Manual address search inline in the modal */}
              <ManualSearchSection>
                <ManualSearchLabel>Or search your delivery area:</ManualSearchLabel>
                <Combobox onSelect={handleManualAddressSelect}>
                  <div style={{ position: "relative" }}>
                    <ComboboxInput
                      value={manualAddressInput}
                      onChange={(e) => {
                        setManualAddressInput(e.target.value);
                        if (manualPickError) setManualPickError(null);
                      }}
                      disabled={!ready}
                      placeholder={addressPlaceholder}
                      style={{
                        width: "100%",
                        padding: "12px 14px",
                        border: "1px solid #ddd",
                        borderRadius: "8px",
                        fontSize: "0.9rem",
                        outline: "none",
                        boxSizing: "border-box",
                      }}
                    />
                    <AddressSuggestions
                      portal={false}
                      style={{
                        backgroundColor: "white",
                        margin: "0",
                        padding: "0",
                        fontSize: "13px",
                        border: "1px solid #ddd",
                        boxShadow: "0 4px 16px rgba(0, 0, 0, 0.1)",
                        borderBottomRightRadius: "8px",
                        borderBottomLeftRadius: "8px",
                        zIndex: 10001,
                        position: "absolute",
                        width: "100%",
                      }}
                    >
                      <ComboboxList
                        style={{
                          listStyle: "none",
                          margin: 0,
                          padding: 0,
                        }}
                      >
                        {suggestions.map(({ place_id, description }) => (
                          <ComboboxOption
                            key={place_id}
                            value={description}
                            style={{
                              padding: "10px 14px",
                              cursor: "pointer",
                              borderBottom: "1px solid #f0f0f0",
                            }}
                            onMouseDown={(e) => {
                              e.preventDefault();
                              handleManualAddressSelect(description);
                            }}
                          />
                        ))}
                      </ComboboxList>
                    </AddressSuggestions>
                  </div>
                </Combobox>
                  <AddressSearchStatus loading={addressLoading} ready={ready} status={addressStatus} count={suggestions.length} error={placesError} />
                {manualPickError && (
                  <ManualPickErrorText>{manualPickError}</ManualPickErrorText>
                )}
              </ManualSearchSection>

              <SecondaryButton onClick={handleClose}>
                Maybe Later
              </SecondaryButton>
            </ButtonGroup>

            <PrivacyNote>
              Your location is only used to show relevant delivery options and is stored locally on your device.
            </PrivacyNote>
          </PromptCard>
        </Overlay>
      )}
    </AnimatePresence>
  );
};

export default LocationPrompt;

// Styled Components
const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  padding: 20px;
`;

const PromptCard = styled.div`
  background: white;
  border-radius: 16px;
  padding: 32px;
  max-width: 400px;
  width: 100%;
  text-align: center;
  position: relative;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
`;

const CloseButton = styled.button`
  position: absolute;
  top: 12px;
  right: 12px;
  background: none;
  border: none;
  cursor: pointer;
  color: #666;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: background 0.2s;

  &:hover {
    background: #f0f0f0;
  }
`;

const IconWrapper = styled.div`
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;
`;

const Title = styled.h2`
  font-size: 1.5rem;
  font-weight: 700;
  color: #333;
  margin: 0 0 12px;
`;

const Description = styled.p`
  font-size: 0.95rem;
  color: #666;
  line-height: 1.5;
  margin: 0 0 20px;
`;

const ErrorMessage = styled.div`
  background: #ffebee;
  color: #c62828;
  padding: 12px;
  border-radius: 8px;
  font-size: 0.85rem;
  margin-bottom: 16px;
`;

const PermissionNote = styled.div`
  background: #fff3e0;
  color: #e65100;
  padding: 12px;
  border-radius: 8px;
  font-size: 0.85rem;
  margin-bottom: 16px;
  line-height: 1.5;
`;

const ApproxLocationBanner = styled.div`
  background: linear-gradient(135deg, #e3f2fd 0%, #e8f5e9 100%);
  border: 1px solid #bbdefb;
  padding: 14px;
  border-radius: 10px;
  font-size: 0.9rem;
  margin-bottom: 16px;
  color: #1565c0;
  line-height: 1.5;
`;

const ApproxSubtext = styled.div`
  font-size: 0.78rem;
  color: #666;
  margin-top: 6px;
`;

const ButtonGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const PrimaryButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 14px 24px;
  background: linear-gradient(135deg, #1976d2 0%, #1565c0 100%);
  color: white;
  border: none;
  border-radius: 10px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(25, 118, 210, 0.4);
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

const SecondaryButton = styled.button`
  padding: 12px 24px;
  background: transparent;
  color: #666;
  border: 1px solid #ddd;
  border-radius: 10px;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #f5f5f5;
    border-color: #ccc;
  }
`;

const PrivacyNote = styled.p`
  font-size: 0.75rem;
  color: #999;
  margin: 20px 0 0;
  line-height: 1.4;
`;

// Compact version styles with dropdown
const CompactWrapper = styled.div`
  position: relative;
  overflow: visible;
  z-index: 100;
`;

const CompactContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: #2e97d0a3;
  border-radius: 20px;
  cursor: pointer;
  transition: background 0.2s;
  max-width: 200px;
  min-width: 0;
  overflow: hidden;

  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }

  @media only screen and (max-width: 480px) {
    max-width: 100%;
    width: 100%;
    padding: 5px 10px;
    gap: 4px;
  }

  @media only screen and (max-width: 360px) {
    max-width: 100%;
    width: 100%;
    padding: 4px 8px;
  }
`;

const CompactText = styled.span`
  font-size: 0.8rem;
  color: ${props => props.$clickable ? '#57f1f9ff' : '#f6fafeff'};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-weight: ${props => props.$clickable ? '500' : '400'};
`;

const DropdownMenu = styled.div`
  color: #e7edf4;
  [data-location-error] { color: #ffb4a6; }
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  min-width: 280px;
  width: min(380px, calc(100vw - 32px));
  box-sizing: border-box;
  max-height: min(720px, calc(100dvh - 120px));
  overflow-y: auto;
  overscroll-behavior: contain;
  background: #2c3e50;
  border-radius: 12px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
  padding: 16px;
  z-index: 10000;

  /* Center on mobile screens */
  @media (max-width: 480px) {
    position: fixed;
    left: 16px;
    right: 16px;
    top: auto;
    width: auto;
    min-width: unset;
    max-width: none;
  }
`;

const AddressSuggestions = styled(ComboboxPopover)`
  color: #243746;
  max-height: 240px;
  overflow-y: auto;
  overscroll-behavior: contain;
  box-sizing: border-box;

  [data-reach-combobox-option] {
    color: #243746;
    background: #fff;
    min-height: 44px;
    box-sizing: border-box;
    line-height: 1.45;
    overflow-wrap: anywhere;
  }
  [data-reach-combobox-option]:hover,
  [data-reach-combobox-option][aria-selected="true"] {
    color: #173c29;
    background: #e8f5e9;
  }
`;

const DropdownHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  color: #fff;
  margin-bottom: 12px;
  font-size: 0.95rem;
`;

const CurrentLocationDisplay = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  background: #e8f5e9;
  border-radius: 8px;
  margin-bottom: 12px;
  font-size: 0.85rem;
  color: #2e7d32;

  span {
    flex: 1;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const ClearButtonSmall = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  cursor: pointer;
  color: #999;
  padding: 2px;
  border-radius: 50%;
  transition: all 0.2s;

  &:hover {
    background: #c8e6c9;
    color: #666;
  }
`;

const DropdownButton = styled.button`
  display: flex;
  align-items: center;
  width: 100%;
  padding: 10px 12px;
  background: #34495e;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 0.85rem;
  color: #fff;
  transition: background 0.2s;

  &:hover:not(:disabled) {
    background: #3d5a73;
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

const ErrorText = styled.div.attrs({ "data-location-error": true })`
  color: #c62828;
  font-size: 0.75rem;
  margin-top: 8px;
`;



const PermissionDeniedCard = styled.div`
  background: rgba(255, 152, 0, 0.08);
  border: 1px solid rgba(255, 152, 0, 0.25);
  border-radius: 8px;
  padding: 10px;
  margin-top: 6px;
`;

const PermissionDeniedRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-bottom: 8px;
`;

const PermissionDeniedText = styled.span`
  font-size: 0.75rem;
  color: #e0e0e0;
  line-height: 1.4;
`;

const EnableGpsButton = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px 12px;
  background: linear-gradient(135deg, #ff9800 0%, #f57c00 100%);
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 0.78rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: linear-gradient(135deg, #ffa726 0%, #ff9800 100%);
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }
`;

const InstructionSteps = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 10px;
  padding: 8px 10px;
  background: rgba(255, 255, 255, 0.04);
  border-radius: 6px;
`;

const InstructionStep = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
`;

const StepNumber = styled.span`
  font-size: 0.72rem;
  font-weight: 700;
  color: #ff9800;
  min-width: 14px;
  flex-shrink: 0;
`;

const StepText = styled.span`
  font-size: 0.72rem;
  color: #bdc3c7;
  line-height: 1.5;
`;

const ApproxBadge = styled.span`
  font-size: 0.65rem;
  font-weight: 600;
  color: #e65100;
  background: #fff3e0;
  padding: 2px 6px;
  border-radius: 4px;
  white-space: nowrap;
  flex-shrink: 0;
`;




const ManualSearchSection = styled.div`
  text-align: left;
`;

const ManualSearchLabel = styled.div`
  font-size: 0.85rem;
  color: #555;
  margin-bottom: 8px;
  font-weight: 500;
`;

const ManualPickErrorText = styled.div`
  color: #c62828;
  background: #ffebee;
  border-radius: 6px;
  padding: 8px 10px;
  font-size: 0.8rem;
  margin-top: 8px;
  line-height: 1.4;
`;

/* Stale-location re-confirm ("Still at X?") card in the navbar dropdown */
const ReconfirmCard = styled.div`
  background: rgba(255, 152, 0, 0.08);
  border: 1px solid rgba(255, 152, 0, 0.3);
  border-radius: 10px;
  padding: 12px;
  margin-bottom: 10px;
`;

const ReconfirmHeader = styled.div`
  font-size: 0.85rem;
  font-weight: 600;
  color: #ffcc80;
  margin-bottom: 4px;
`;

const ReconfirmNote = styled.div`
  font-size: 0.72rem;
  color: #bdc3c7;
  line-height: 1.4;
  margin-bottom: 10px;
`;

/* IP Suggestion Confirmation Card */
const IpSuggestionCard = styled.div`
  background: linear-gradient(135deg, #2c3e50 0%, #34495e 100%);
  border: 1px solid #4a6785;
  border-radius: 10px;
  padding: 14px;
  margin-bottom: 10px;
`;

const IpSuggestionHeader = styled.div`
  font-size: 0.85rem;
  font-weight: 600;
  color: #ecf0f1;
  margin-bottom: 6px;
`;

const IpSuggestionAddress = styled.div`
  font-size: 0.9rem;
  font-weight: 500;
  color: #fff;
  margin-bottom: 4px;
`;

const IpSuggestionNote = styled.div`
  font-size: 0.7rem;
  color: #bdc3c7;
  margin-bottom: 10px;
`;

const IpSuggestionActions = styled.div`
  display: flex;
  gap: 8px;
`;

const IpConfirmButton = styled.button`
  flex: 1;
  padding: 8px 12px;
  background: #27ae60;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 0.78rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: #2ecc71;
  }
`;

const IpDismissButton = styled.button`
  flex: 1;
  padding: 8px 12px;
  background: transparent;
  color: #bdc3c7;
  border: 1px solid #4a6785;
  border-radius: 6px;
  font-size: 0.78rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: rgba(255, 255, 255, 0.05);
    border-color: #7f8c8d;
  }
`;

const GpsNudge = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-top: 12px;
  padding-top: 10px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
`;




const GpsNudgeText = styled.span`
  font-size: 0.72rem;
  color: #81c784;
  line-height: 1.5;

  strong {
    color: #a5d6a7;
  }
`;

const GpsPrecisionTag = styled.div`
  font-size: 0.68rem;
  color: #7f8c8d;
  text-align: center;
  margin-top: 4px;
  letter-spacing: 0.3px;
`;

const Divider = styled.div`
  height: 1px;
  background: #34495e;
  margin: 12px 0;
`;

const ManualInputLabel = styled.div`
  font-size: 0.8rem;
  color: #bdc3c7;
  margin-bottom: 8px;
`;

/* Unused styled components - kept for potential future use
const ManualInputContainer = styled.div`
  display: flex;
  gap: 8px;
`;

const ManualInput = styled.input`
  flex: 1;
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 0.85rem;
  
  &:focus {
    outline: none;
    border-color: #1976d2;
  }
`;

const SaveButton = styled.button`
  padding: 8px 16px;
  background: ${props => props.disabled ? '#ccc' : '#1976d2'};
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 0.85rem;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: background 0.2s;

  &:hover:not(:disabled) {
    background: #1565c0;
  }
`;
*/
